import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { SlInputComponent } from '../sl-input/sl-input.component';
import { SlMenuItemComponent } from '../sl-menu-item/sl-menu-item.component';
import { SlDropdownInterface } from '../utils/global-interfaces/sl-global.interfaces';
import { SlTypographyComponent } from '../sl-typography/sl-typography.component';

@Component({
  selector: 'sl-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SlMenuItemComponent,
    SlInputComponent,
    SlTypographyComponent,
  ],
  templateUrl: './sl-dropdown.component.html',
  styleUrl: './sl-dropdown.component.scss',
})
export class SlDropdownComponent<T> implements OnChanges {
  @Input()
  name = '';
  @Input()
  placeholder = '';
  @Input()
  label = '';
  @Input()
  disabled = false;
  @Input({ required: true })
  suggestions: SlDropdownInterface<T>[] = [];
  @Input()
  defaultSelectedSuggestion?: SlDropdownInterface<T>;
  @Input() control: FormControl = new FormControl({
    value: undefined,
    disabled: true,
  });
  @Input() errorMessages: { [key: string]: string } = {};
  @Input() optionsDisabled: T[] = [];
  @Input()
  lightTheme = false;
  @Input()
  variant: 'standard' | 'header' = 'standard';
  @Output()
  selectedValueEvent = new EventEmitter();

  @ViewChild('outerButton')
  outerButton!: ElementRef;

  @ViewChild('suggestionsContainer')
  suggestionsContainer!: ElementRef;

  selectedSuggestion: SlDropdownInterface<T> | undefined;

  displaySuggestions = false;

  focusedElement?: EventTarget;

  ngOnChanges() {
    if (this.defaultSelectedSuggestion) {
      this.selectedSuggestion = this.defaultSelectedSuggestion;

      if (this.defaultSelectedSuggestion.key) {
        this.control.setValue(this.defaultSelectedSuggestion.key);
      }
    } else if (this.suggestions[0]?.key) {
      this.selectedSuggestion = this.suggestions[0];
    }
  }

  onClickSuggestion(clickedSuggestion: SlDropdownInterface<T>) {
    if (clickedSuggestion) {
      this.selectedSuggestion = clickedSuggestion;
      this.control.setValue(clickedSuggestion.key);
      this.selectedValueEvent.emit(clickedSuggestion.value);
    }

    this.displaySuggestions = false;
    if (this.variant === 'header') {
      this.outerButton?.nativeElement?.focus();
    } else if (this.variant === 'standard') {
      document.getElementById(`sl-dropdown-input-${this.name}-button`)?.focus();
    }
  }

  handleDisplaySuggestions(disabled: boolean) {
    if (disabled) return;

    if (this.suggestions.length) {
      this.displaySuggestions = !this.displaySuggestions;
      if (this.displaySuggestions) {
        this.control.markAsUntouched();
        if (!this.control.value) {
          this.selectedSuggestion = undefined;
        }
      } else {
        this.control.markAllAsTouched();
      }
    }
  }

  onKeyDownSuggestion(event: KeyboardEvent) {
    const srcElement = event.target as HTMLElement;
    const spc = event.key === ' ';
    const enter = event.key === 'Enter';
    const down = event.key === 'ArrowDown';
    const up = event.key === 'ArrowUp';
    const esc = event.key === 'Escape';
    const tab = event.key === 'Tab';
    const firstChild: HTMLElement = srcElement?.parentElement?.parentElement
      ?.firstElementChild?.firstElementChild as HTMLElement;
    const lastChild: HTMLElement = srcElement?.parentElement?.parentElement
      ?.lastElementChild?.lastElementChild as HTMLElement;
    const nextSibling: HTMLElement = srcElement?.parentElement
      ?.nextElementSibling?.firstElementChild as HTMLElement;
    const prevSibling: HTMLElement = srcElement?.parentElement
      ?.previousElementSibling?.firstElementChild as HTMLElement;

    if (tab || enter) {
      return;
    }
    event.preventDefault();
    if (down) {
      if (nextSibling) {
        nextSibling.focus();
      } else {
        firstChild.focus();
      }
    }
    if (up) {
      if (prevSibling) {
        prevSibling.focus();
      } else {
        lastChild.focus();
      }
    }
    if (esc || spc) {
      this.displaySuggestions = false;
      if (this.variant === 'header') {
        this.outerButton?.nativeElement?.focus();
      } else if (this.variant === 'standard') {
        document
          .getElementById(`sl-dropdown-input-${this.name}-button`)
          ?.focus();
      }
    }
  }

  onKeyDownButton(event: KeyboardEvent) {
    const down = event.key === 'ArrowDown';
    const up = event.key === 'ArrowUp';
    const esc = event.key === 'Escape';
    const spc = event.key === ' ';
    if (down) {
      event.preventDefault();
      this.suggestionsContainer?.nativeElement?.firstElementChild?.firstElementChild?.focus();
    } else if (up) {
      event.preventDefault();
      this.suggestionsContainer?.nativeElement?.lastElementChild?.lastElementChild?.focus();
    } else if (spc) {
      event.preventDefault();
      this.displaySuggestions = !this.displaySuggestions;
    } else if (esc && this.displaySuggestions) {
      this.displaySuggestions = false;
    }
  }

  checkFocus(event: FocusEvent) {
    const relatedTarget = event.relatedTarget as Element;
    const isClickingInputButton =
      relatedTarget?.id === 'sl-dropdown-input-' + this.name;
    const isClickingInput =
      relatedTarget?.id === 'sl-dropdown-input-' + this.name + '-input';
    const isFocusingDropdownButton =
      relatedTarget?.id === 'keyboard_arrow_up-button' ||
      relatedTarget?.id === 'keyboard_arrow_down-button';
    const isFocusingMenuItem = relatedTarget?.id?.startsWith('sl-menu-item-');

    if (
      !(
        isFocusingDropdownButton ||
        isFocusingMenuItem ||
        isClickingInput ||
        isClickingInputButton
      )
    ) {
      this.displaySuggestions = false;
    }
  }

  setFocusedElement(event?: Event) {
    this.focusedElement = event ? (event.target as HTMLElement) : undefined;
  }

  getTypographyColor() {
    return this.lightTheme
      ? 'var(--color-neutral-40)'
      : 'var(--color-neutral-10)';
  }

  getTypographyVariant() {
    return this.lightTheme ? 'input' : 'button-10';
  }
}
